import { useCallback, useEffect, useState } from 'react';

import { useDashboardHost } from '../context/DashboardHostContext';
import {
  resolveEntityPropertyText,
  type EntityPropertyTable,
} from '../dashboard/widget/widgets/textFromProperty/resolveEntityPropertyText';

// -- type definitions --

export interface UseEntityPropertyDisplayTextResult {
  readonly text: string | undefined;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly reload: () => Promise<void>;
}

// -- main hook --

/**
 * Loads the display text for a text-from-entity-property widget from host catalogs.
 */
export function useEntityPropertyDisplayText(
  entityTable: EntityPropertyTable,
  rowId: number,
  property: string
): UseEntityPropertyDisplayTextResult {
  const host = useDashboardHost();
  const [text, setText] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [projects, sites] = await Promise.all([
        host.api.project.getAll(),
        host.api.site.getAll(),
      ]);
      setText(
        resolveEntityPropertyText(
          entityTable,
          rowId,
          property,
          projects,
          sites
        )
      );
    } catch (loadError) {
      setText(undefined);
      setError(
        loadError instanceof Error ? loadError : new Error(String(loadError))
      );
    } finally {
      setLoading(false);
    }
  }, [entityTable, host.api.project, host.api.site, property, rowId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { text, loading, error, reload };
}
